import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowRight, FileAudio, UploadCloud, X } from 'lucide-react';
import { useLanguage } from '../../../assets/js/LanguageContext';
import FormField from '../../common/FormField';
import LimitExceededModal from './LimitExceededModal';
import Loader from '../../common/Loader';
import Modal from '../../common/Modal';
import SectionEyebrow from '../../common/SectionEyebrow';
import '../../../assets/styles/ChooseSurvey.css';
import '../../../assets/styles/TemplateBuilder.css';
import '../../../assets/styles/SurveyAnswer.css';

const UploadModal = ({ onAnalysisComplete, onBack }) => {
  const { t, language } = useLanguage();
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileType, setFileType] = useState('');
  const [transcription, setTranscription] = useState('');
  const [showPopup, setShowPopup] = useState(false);
  const [excelData, setExcelData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [surveyName, setSurveyName] = useState('');
  const [showLimitMessage, setShowLimitMessage] = useState(false);
  const [limitInfo, setLimitInfo] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isLargeFile, setIsLargeFile] = useState(false);
  const [alert, setAlert] = useState(null);
  const [nameError, setNameError] = useState(null);

  const navigate = useNavigate();

  const getToken = () => {
    return localStorage.getItem('token');
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const extension = file.name.split('.').pop().toLowerCase();
      const supportedExtensions = ['mp3', 'wav', 'm4a', 'xls', 'xlsx', 'csv'];
      
      if (!supportedExtensions.includes(extension)) {
        setAlert({ title: t('upload_format_title'), text: t('upload_unsupported_format') });
        // Limpiar el input de archivo
        const fileInput = document.getElementById('fileInput');
        if (fileInput) {
          fileInput.value = '';
        }
        return;
      }
      
      setSelectedFile(file);
      if (['mp3', 'wav', 'm4a'].includes(extension)) {
        setFileType('audio');
        // Determinar si es un archivo grande (> 5MB para audio)
        const fileSizeMB = file.size / (1024 * 1024);
        setIsLargeFile(fileSizeMB > 5);
      } else if (['xls', 'xlsx', 'csv'].includes(extension)) {
        setFileType('excel');
        setIsLargeFile(false);
      }
    } else {
      setSelectedFile(null);
      setFileType('');
      setIsLargeFile(false);
    }
    setTranscription('');
    setShowPopup(false);
    setExcelData(null);
    setIsLoading(false);
    setSurveyName('');
    setUploadProgress(0);
  };

  const clearSelectedFile = () => {
    setSelectedFile(null);
    setFileType('');
    setTranscription('');
    setShowPopup(false);
    setExcelData(null);
    setIsLoading(false);
    setSurveyName('');
    setUploadProgress(0);
    setIsLargeFile(false);
    // Limpiar el input de archivo
    const fileInput = document.getElementById('fileInput');
    if (fileInput) {
      fileInput.value = '';
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !fileType) return;

    setIsLoading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('lang', language);

    const token = getToken();
    if (!token) {
      setAlert({ title: t('upload_alert_title'), text: t('auth_token_missing') });
      setIsLoading(false);
      return;
    }

    // Debug: Verificar el token
    try {
      const tokenPayload = JSON.parse(atob(token.split('.')[1]));
      console.log('Token payload:', tokenPayload);
      const userId = tokenPayload.userId || tokenPayload.id_usuario;
      console.log('User ID from token:', userId);
    } catch (error) {
      console.error('Error al decodificar token:', error);
    }

    try {
      // Seleccionar endpoint según el tipo y tamaño del archivo
      let endpoint;
      let timeout = 30000; // 30 segundos por defecto
      
      if (fileType === 'excel') {
        endpoint = `${process.env.REACT_APP_AI_URL}/upload-excel`;
        timeout = 30000;
      } else if (fileType === 'audio') {
        if (isLargeFile) {
          // Para archivos grandes, usar el endpoint normal con timeout extendido
          endpoint = `${process.env.REACT_APP_AI_URL}/upload-audio`;
          timeout = 600000; // 10 minutos para archivos grandes
        } else {
          // Para archivos pequeños, usar el endpoint rápido
          endpoint = `${process.env.REACT_APP_AI_URL}/upload-audio-fast`;
          timeout = 60000; // 1 minuto para archivos pequeños
        }
      }

      console.log(`Usando endpoint: ${endpoint} para archivo ${isLargeFile ? 'grande' : 'pequeño'}`);

      // Crear AbortController para manejar timeouts
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      // Simular progreso para archivos grandes
      if (isLargeFile) {
        const progressInterval = setInterval(() => {
          setUploadProgress(prev => {
            if (prev < 85) return prev + Math.random() * 5;
            return prev;
          });
        }, 3000);
        
        // Limpiar el intervalo cuando termine la carga
        setTimeout(() => clearInterval(progressInterval), timeout - 2000);
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: formData,
        signal: controller.signal
      });

      clearTimeout(timeoutId);
      setUploadProgress(100);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: t('common_unknown_error') }));
        throw new Error(`${t('upload_error_prefix')}: ${errorData.message || response.statusText}`);
      }

      const data = await response.json();

      if (fileType === 'audio') {
        // Para archivos de audio, mostrar transcripción
        setTranscription(data.transcription);
        setShowPopup(true);
      } else if (fileType === 'excel') {
        // Para archivos Excel, mostrar datos y convertir a texto
        setExcelData(data);
        
        setShowPopup(true);
      }

    } catch (error) {
      console.error('Error al subir archivo:', error);
      
      let errorMessage = error.message;
      
      // Manejar diferentes tipos de errores
      if (error.name === 'AbortError') {
        errorMessage = t('upload_error_timeout');
      } else if (error.message.includes('Broken pipe')) {
        errorMessage = t('upload_error_broken_pipe');
      } else if (error.message.includes('recognition connection failed')) {
        errorMessage = t('upload_error_recognition');
      }
      
      setAlert({ title: t('upload_alert_title'), text: `${t('upload_error_prefix')}: ${errorMessage}` });
    } finally {
      setIsLoading(false);
      setUploadProgress(0);
    }
  };

  const handleCreateDiagnostic = async () => {
    if (!surveyName.trim()) {
      setNameError(t('upload_diagnostic_name_required'));
      return;
    }

    setIsLoading(true);

    const currentDate = new Date();
    const formattedDate = currentDate.toISOString().split('T')[0];
    const token = getToken();
    
    if (!token) {
      console.warn(t('auth_token_missing'));
      return;
    }

    try {
      // Excel: el servidor convierte cada columna en una pregunta con todas sus respuestas.
      // Audio: una sola pregunta abierta con la transcripción.
      const requestData = {
        titulo: surveyName,
        fecha_inicio: formattedDate,
        fecha_fin: formattedDate,
        lang: language,
        ...(fileType === 'excel'
          ? { excel: { columns: excelData.columns, rows: excelData.rows } }
          : { transcription }),
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/create-diagnostic`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: t('common_unknown_error') }));
        
        console.error('Error del servidor:', errorData);
        
        // Verificar si es un error de límite de encuestas
        if (response.status === 403 && errorData.detalles) {
          // Guardamos el response completo (mensaje + detalles) para no perder info.
          setLimitInfo(errorData);
          setShowLimitMessage(true);
          setIsLoading(false);
          return;
        }
        
        throw new Error(errorData.message || errorData.error || String(response.status));
      }

      const data = await response.json();
      console.log('Diagnóstico creado exitosamente:', data);

      if (onAnalysisComplete) {
        onAnalysisComplete({ transcription, excelData, fileType });
      }
      navigate('/dashboard');
    } catch (error) {
      console.error('Error al crear el diagnóstico:', error);
      setAlert({ title: t('upload_alert_title'), text: `${t('create_diagnostic_error_prefix')}: ${error.message}` });
    } finally {
      setIsLoading(false);
      setShowPopup(false);
    }
  };

  const closePopup = () => {
    setShowPopup(false);
    if (onAnalysisComplete) {
      onAnalysisComplete({ transcription, excelData, fileType });
    }
    navigate('/dashboard');
  };

  const columns = Array.isArray(excelData && excelData.columns) ? excelData.columns : [];
  const rows = Array.isArray(excelData && excelData.rows) ? excelData.rows : [];
  const shownRows = rows.slice(0, 8);
  const fileSizeMb = selectedFile ? (selectedFile.size / (1024 * 1024)).toFixed(1) : null;

  return (
    <div className="tp-choose tp-upload">
      {onBack && (
        <button type="button" className="tp-choose__back" onClick={onBack}>
          <ArrowLeft size={16} strokeWidth={2} aria-hidden="true" />
          {t('common_back')}
        </button>
      )}

      <header className="tp-choose__head">
        <div className="tp-choose__heading">
          <SectionEyebrow>{t('choose_process')}</SectionEyebrow>
          <h1 className="tp-choose__title">{t('upload_title')}</h1>
          <p className="tp-choose__lead">{t('upload_lead')}</p>
        </div>
      </header>

      <section className="tp-form tp-upload__card">
        <label
          className={`tp-upload__drop ${selectedFile ? 'is-filled' : ''}`.trim()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => {
            e.preventDefault();
            const file = e.dataTransfer.files[0];
            if (file) handleFileChange({ target: { files: [file] } });
          }}
        >
          <input
            id="fileInput"
            type="file"
            accept=".xlsx,.xls,.csv,.mp3,.wav,.m4a"
            onChange={handleFileChange}
            disabled={isLoading}
            className="tp-sr-only"
          />
          <span className="tp-upload__icon" aria-hidden="true">
            {fileType === 'audio' ? <FileAudio size={26} strokeWidth={1.75} /> : <UploadCloud size={26} strokeWidth={1.75} />}
          </span>
          <span className="tp-upload__drop-title">
            {selectedFile ? selectedFile.name : t('upload_dropzone')}
          </span>
          <span className="tp-upload__drop-hint">
            {selectedFile
              ? `${fileSizeMb} MB${isLargeFile ? ' - ' + t('upload_large_file_badge') : ''}`
              : t('upload_click_select')}
          </span>

          {selectedFile && !isLoading && (
            <button
              type="button"
              className="tp-upload__clear"
              onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                clearSelectedFile();
              }}
              aria-label={t('ai_docs_remove_file')}
              title={t('ai_docs_remove_file')}
            >
              <X size={16} strokeWidth={2.25} aria-hidden="true" />
            </button>
          )}
        </label>

        <p className="tp-upload__formats">{t('upload_supported_formats')}</p>
        {isLargeFile && <p className="tp-upload__formats">{t('upload_large_audio_hint')}</p>}

        {isLoading && (
          <div className="tp-upload__progress">
            <span className="tp-answer__bar">
              <span style={{ width: `${uploadProgress}%` }} />
            </span>
            <span className="tp-upload__progress-text">
              {isLargeFile ? t('upload_processing_large') : t('upload_processing_file')}
            </span>
          </div>
        )}

        <div className="tp-upload__actions">
          {selectedFile && !isLoading && (
            <button
              type="button"
              className="btn btn--ghost"
              onClick={clearSelectedFile}
            >
              {t('common_cancel')}
            </button>
          )}
          <button
            type="button"
            className="btn btn--primary"
            onClick={handleUpload}
            disabled={!selectedFile || isLoading}
          >
            <UploadCloud size={16} strokeWidth={2} aria-hidden="true" />
            {t('upload_process_file')}
          </button>
        </div>
      </section>

      {transcription && (
        <section className="tp-builder__panel">
          <div className="tp-builder__panel-head">
            <h2 className="tp-builder__count">{t('upload_audio_transcription')}</h2>
          </div>
          <p className="tp-upload__transcription">{transcription}</p>
          <p className="tp-upload__formats">{t('upload_audio_segments')}</p>
          <div className="tp-builder__panel-actions">
            <span />
            <button type="button" className="btn btn--primary" onClick={() => setShowPopup(true)}>
              {t('create_survey_submit')}
              <ArrowRight size={16} strokeWidth={2} aria-hidden="true" />
            </button>
          </div>
        </section>
      )}

      {excelData && (
        <section className="tp-builder__panel">
          <div className="tp-builder__panel-head">
            <h2 className="tp-builder__count">{t('upload_excel_data')}</h2>
            <span className="tp-upload__meta">
              {t('upload_excel_columns_summary').replace('{count}', columns.length)} ·{' '}
              {t('upload_excel_rows_summary').replace('{shown}', shownRows.length).replace('{total}', rows.length)}
            </span>
          </div>

          <div className="tp-upload__table-wrap">
            <table className="tp-upload__table">
              <thead>
                <tr>
                  {columns.map((column, index) => <th key={index}>{column}</th>)}
                </tr>
              </thead>
              <tbody>
                {shownRows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {columns.map((column, colIndex) => (
                      <td key={colIndex}>{row[colIndex] === undefined || row[colIndex] === null || row[colIndex] === '' ? t('upload_excel_empty_cell') : String(row[colIndex])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {rows.length > shownRows.length && (
            <p className="tp-upload__formats">{t('upload_excel_truncated_hint')}</p>
          )}

          <div className="tp-builder__panel-actions">
            <span />
            <button type="button" className="btn btn--primary" onClick={() => setShowPopup(true)}>
              {t('create_survey_submit')}
              <ArrowRight size={16} strokeWidth={2} aria-hidden="true" />
            </button>
          </div>
        </section>
      )}

      {isLoading && (
        <Loader overlay caption={isLargeFile ? t('upload_large_files_wait') : t('upload_processing_file')} />
      )}

      <Modal
        open={showPopup}
        onOpenChange={(open) => { if (!open) setShowPopup(false); }}
        variant="edit"
        title={t('create_survey_modal_title')}
        description={t('upload_name_lead')}
        cancelText={t('common_cancel')}
        confirmText={t('create_survey_submit')}
        onCancel={() => setShowPopup(false)}
        onConfirm={() => {
          if (!surveyName.trim()) {
            setNameError(t('upload_diagnostic_name_required'));
            return false;
          }
          handleCreateDiagnostic();
          return false;
        }}
        size="md"
      >
        <div className="tp-form tp-form--bare">
          <FormField
            id="upload-survey-name"
            label={t('create_diagnostic_name')}
            type="text"
            value={surveyName}
            placeholder={t('create_name_placeholder')}
            onChange={(e) => { setSurveyName(e.target.value); setNameError(null); }}
            error={nameError}
          />
        </div>
      </Modal>

      <LimitExceededModal
        isOpen={showLimitMessage}
        onClose={() => { setShowLimitMessage(false); closePopup(); }}
        limitInfo={limitInfo}
      />

      <Modal
        open={Boolean(alert)}
        onOpenChange={(open) => { if (!open) setAlert(null); }}
        variant="warning"
        title={alert ? alert.title : ""}
        description={alert ? alert.text : ""}
        confirmText={t("common_understood")}
      />
    </div>
  );
};

export default UploadModal;
